import { SafeHtml } from "@angular/platform-browser";
import { EDeviceCategory } from "../conf/device-categories";
import { Device } from "./device";

export class FirmwareVersions {
	Versions: FirmwareUpdate[];
}

export class FirmwareUpdate {
	Description: string;
	DeviceFWVersion: string;
	AvailableFWVersion: string;
	AvailableUpdate: boolean;
	CanBeUpdated?: boolean;
}

export class FirmwareUpdateDevice extends FirmwareVersions {
	Description: string;
	DeviceFWVersion: string;
	AvailableFWVersion: string;
	AvailableUpdate: boolean;
	CanBeUpdated?: boolean;
}


export class PostFirmwareUpdate {
	Component: string;
}

export class FirmwaresCategory {
	automations: FirmwaresDeviceAutomations;
	gateways: FirmwaresDeviceGateways;
	videoentrysystems: FirmwaresDeviceVideoEntry;
}

export class FirmwaresDeviceAutomations {
	rbe4: FirmwaresRepo;
	srn1: FirmwaresRepo;
	zbb2: FirmwaresRepo;
	zbb3: FirmwaresRepo;
	zbc1: FirmwaresRepo;
	zbkn: FirmwaresRepo;
	zbx7n: FirmwaresRepo;
	zc3n: FirmwaresRepo;
	zl392: FirmwaresRepo;
	zl392c: FirmwaresRepo;
	zl392d: FirmwaresRepo;
	zl57: FirmwaresRepo;
	zl65: FirmwaresRepo;
	zlb24s: FirmwaresRepo;
	zlb30a: FirmwaresRepo;
	zlb30b: FirmwaresRepo;
	zlx230p: FirmwaresRepo;
	zlx24m: FirmwaresRepo;
	zlx24s: FirmwaresRepo;
	zn7: FirmwaresRepo;
	zn7v: FirmwaresRepo;
	zn8: FirmwaresRepo;
	zn8rev: FirmwaresRepo;
}


export class FirmwaresDeviceGateways {
	camekey: FirmwaresRepo;
	camekeygw: FirmwaresRepo;
	mtmac: FirmwaresRepo;
	mtmacus: FirmwaresRepo;
	qbe: FirmwaresRepo;
	qbesh: FirmwaresRepo;
}
export class FirmwaresDeviceVideoEntry {
	lhs: FirmwaresRepo;
	lhsbt: FirmwaresRepo;
	lhspill: FirmwaresRepo;
	mtmaclte: FirmwaresRepo;
	mtmacuslte: FirmwaresRepo;
	mtmbfvs: FirmwaresRepo;
	mtmdy: FirmwaresRepo;
	mtmdyfonts: FirmwaresRepo;
	mtmrfid: FirmwaresRepo;
}

export class FirmwaresRepo {
	0: Firmware[];
}


export class Firmware {
	version: string;
	date: string;
	releaseNotes: any;
	releaseNotesMd: any;
	isTest: boolean;
	releaseNoteHtml: SafeHtml;
}

const normalizeToken = (value?: string): string => (value || '').trim().toLowerCase();

const matchesAnyToken = (description: string, tokens: string[]): boolean => {
	const desc = normalizeToken(description);
	if (!desc) {
		return false;
	}
	return tokens
		.map(normalizeToken)
		.filter(Boolean)
		.some(token => desc === token || desc.includes(token) || token.includes(desc));
};

export const splitGatewayFirmwareUpdates = (
	updates: FirmwareUpdate[],
	automationNames: string[],
	gatewayTokens: string[]
): { gateway: FirmwareUpdate[]; automations: FirmwareUpdate[] } => {
	const gateway: FirmwareUpdate[] = [];
	const automations: FirmwareUpdate[] = [];
	for (const update of updates || []) {
		if (matchesAnyToken(update.Description, automationNames)) {
			automations.push(update);
		} else if (matchesAnyToken(update.Description, gatewayTokens)) {
			gateway.push(update);
		} else {
			automations.push(update);
		}
	}
	return { gateway, automations };
};

export const firmwareUpdatesFromResponse = (
	data?: Partial<FirmwareUpdateDevice> | null
): FirmwareUpdate[] => {
	if (!data) {
		return [];
	}
	if (data.Versions) {
		return data.Versions.map(version => ({
			...version,
			CanBeUpdated: version.CanBeUpdated ?? data.CanBeUpdated,
		}));
	}
	const single = { ...data };
	delete single.Versions;
	return [single as FirmwareUpdate];
};

export const automationAvailableFirmware = (
	updates: FirmwareUpdate[] | undefined
): FirmwareUpdate | undefined =>
	(updates || []).find(update =>
		update.AvailableUpdate === true && !!update.DeviceFWVersion && !!update.AvailableFWVersion
	);

export const automationCanBeUpdated = (updates: FirmwareUpdate[] | undefined): boolean =>
	automationAvailableFirmware(updates) !== undefined;

export const gwethFamilyFirmwareUpdates = (
	gatewayUpdates: FirmwareUpdate[],
	children: Array<{ name: string; updates: FirmwareUpdate[] }>,
	gatewayTokens: string[]
): FirmwareUpdate[] => {
	const gatewayOwn = splitGatewayFirmwareUpdates(gatewayUpdates || [], [], gatewayTokens).gateway
		.filter(update => update.AvailableUpdate === true || !!update.AvailableFWVersion);
	const fromChildren = (children || []).flatMap(child => {
		const update = automationAvailableFirmware(child.updates);
		return update ? [{ ...update, Description: child.name }] : [];
	});
	return [...gatewayOwn, ...fromChildren];
};

export const firmwareUpdatesForAutomation = (
	updates: FirmwareUpdate[],
	automationName?: string
): FirmwareUpdate[] => {
	if (!automationName) {
		return [];
	}
	return (updates || []).filter(update => matchesAnyToken(update.Description, [automationName]));
};

export const connectedAutomationsForGateway = (
	gatewayId: number | undefined,
	siteDevices: Array<Pick<Device, 'Id' | 'Name' | 'CategoryId' | 'RootId'>> = []
): Array<Pick<Device, 'Id' | 'Name'>> => {
	if (gatewayId == null) {
		return [];
	}
	return siteDevices.filter(
		device => device.CategoryId === EDeviceCategory.Automation && device.RootId === gatewayId
	);
};

export type AutomationFirmwareRow = {
	device: Pick<Device, 'Id' | 'Name'>;
	update?: FirmwareUpdate;
};

export const mapAutomationsToFirmwareUpdates = (
	automations: Array<Pick<Device, 'Id' | 'Name'>>,
	updates: FirmwareUpdate[]
): AutomationFirmwareRow[] =>
	(automations || []).map(device => ({
		device,
		update: firmwareUpdatesForAutomation(updates, device.Name)[0],
	}));

